import { useEffect, useMemo, useState } from 'react';
import { DATA_ADAPTER } from '../config/adapters';
import type {
  PlatformLotteryDraw,
  PlatformLotterySource,
} from '../services/lottery/platformLotteryDrawTypes';

export type {
  PlatformLotteryDraw,
  PlatformLotterySource,
  PlatformLotteryWindow,
} from '../services/lottery/platformLotteryDrawTypes';

/** Live platform lottery draws for God / support Resultados (system-wide). */
export function usePlatformLotteryDraws(dayKey: string, source: PlatformLotterySource | 'all') {
  const [draws, setDraws] = useState<PlatformLotteryDraw[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (DATA_ADAPTER !== 'firebase' || !/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) {
      setDraws([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    void import('../services/lottery/subscribePlatformLotteryDraws').then(
      ({ subscribePlatformLotteryDraws }) => {
        if (cancelled) return;
        unsubscribe = subscribePlatformLotteryDraws(
          dayKey,
          (rows) => {
            setDraws(rows.filter((row) => source === 'all' || row.source === source));
            setError(null);
            setLoading(false);
          },
          (err) => {
            setError(err.message);
            setLoading(false);
          },
        );
      },
    );

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [dayKey, source]);

  const byKey = useMemo(() => {
    const map = new Map<string, PlatformLotteryDraw>();
    for (const row of draws) {
      map.set(`${row.source}:${row.window}`, row);
    }
    return map;
  }, [draws]);

  return { draws, byKey, loading, error };
}
